import { Injectable } from '@angular/core';
import {BehaviorSubject} from "rxjs";
import {Apollo, QueryRef} from "apollo-angular";
import {get_project_credit_outlets} from "./graphql/getSyndications";
import {DataFormatService} from "../services/data-format.service";
import {FundService} from "../funds/fund-service.service";

export enum UpdateSyndicationsPipelineMode {
  ADD,
  REMOVE,
  UPDATE
}

@Injectable({
  providedIn: 'root'
})
export class SyndicationsPipelineService {
  private _permissions: Permissions[] = []
  private permissions = new BehaviorSubject(this._permissions)
  public permissions$ = this.permissions.asObservable();

  private _credits: any[] = [];
  private credits = new BehaviorSubject(this._credits);
  public credits$ = this.credits.asObservable();

  private _projects:any = {};
  private projects = new BehaviorSubject(this._projects);
  public projects$ = this.projects.asObservable();

  private _fundProjectStates:any[] = []
  private fundProjectStates = new BehaviorSubject(this._fundProjectStates);
  public fundProjectStates$ = this.fundProjectStates.asObservable();

  private _syndicationsSequences:any[] = [];
  private syndicationsSequences = new BehaviorSubject(this._syndicationsSequences);
  public syndicationsSequences$ = this.syndicationsSequences.asObservable();

  public stateOrder = ['Specified', 'Unspecified', 'Unlikely', 'Admitted', 'Unassigned']
  public statusOrder = ['Closed', 'Signed LOI', 'Verbal', 'LOI Pending', 'Placement Committee Approved', 'Pre-LOI', 'App Letter']
  public creditOrder = ['Federal LIHTC', 'Federal Energy', 'Federal Historic', 'State LIHTC', 'State Energy', 'State Historic']


  constructor(private apollo:Apollo, private dataFormat: DataFormatService, private fundService:FundService) { }

  private _setPermissions(permissions: Permissions[]){
    this._permissions = permissions
    this.permissions.next(this._permissions)
  }

  private _setSequences(sequences: any[]){
    this._syndicationsSequences = sequences;
    this.syndicationsSequences.next(sequences)
  }

  private _setStates(projectStates:any[]){
    this._fundProjectStates = projectStates
    this.fundProjectStates.next(this._fundProjectStates)
  }

  private _setCredits(credits:any[]){
    this._credits = credits;
    this.credits.next(this._credits)
  }

  private _setProjects(projects:any){
    this._projects = projects;
    this.projects.next(this._projects)
  }

  public getSyndicationsProjects(){
    let subscription = this.apollo.query({
      query: get_project_credit_outlets,
      fetchPolicy: 'network-only'
    }).subscribe(({data}:any) => {
      let result:any = data;
      this._setPermissions(result.me.fundPermissions)
      this._setSequences(result.statusesBySequence)
      this._setStates(result.fund_project_states)
      this._setCredits(result.syndicationsProjectCredits)
      let projects = this._setSyndicationsProjects(result.syndicationsProjectCredits)
      this._setProjects(projects)

      subscription.unsubscribe();
    })
  }

  private _compareSort = (object:any) => {
    let stateKeys = Object.keys(object)
    let sortedOrder:any = {}
    stateKeys.sort((a:string, b:string) => {
      const indexA = this.stateOrder.indexOf(a)
      const indexB = this.stateOrder.indexOf(b)
      if(indexA === -1 && indexB === -1){
        return a.localeCompare(b)
      } else if (indexA === -1){
        return 1
      } else if (indexB === -1){
        return -1;
      } else {
        return indexA - indexB
      }
    })
    for(const key of stateKeys){
      sortedOrder[key] = object[key];
    }
    return sortedOrder;
  }

  private _subSort = (object:any) => {
    let stateKeys = Object.keys(object)
    let sortedOrder:any = {}
    for(let o = 0; o < stateKeys.length; o++){
      let subKeys = Object.keys(object[stateKeys[o]])
      subKeys.sort((a:string, b:string) => {
        const indexA = this.statusOrder.indexOf(a)
        const indexB = this.statusOrder.indexOf(b)
        if(indexA === -1 && indexB === -1){
          return a.localeCompare(b)
        } else if (indexA === -1){
          return 1
        } else if (indexB === -1){
          return -1
        } else {
          return indexA - indexB
        }
      })
      for(let key of subKeys){
        if(!sortedOrder.hasOwnProperty(stateKeys[o])){
          sortedOrder[stateKeys[o]] = {}
        }
        sortedOrder[stateKeys[o]][key] = {}
        sortedOrder[stateKeys[o]][key] = object[stateKeys[o]][key]
      }
    }

    return sortedOrder;
  }

  private _setSyndicationsProjects(creditOutlets:any){
    let dataObject:any = {}

    creditOutlets = this._combineCredts(creditOutlets)
    let sortedOutlets = this._sortSubCredits(creditOutlets)

    for(let i = 0; i < sortedOutlets.length; i++){

      let outlet = sortedOutlets[i];
      let credit = sortedOutlets[i].projectCredit;
      let project = sortedOutlets[i].projectCredit.project;
      let state = sortedOutlets[i].fundProjectState == null ? 'Unassigned' : sortedOutlets[i].fundProjectState.name == 'Exited' || sortedOutlets[i].fundProjectState.name == 'Admitted' ? 'Unspecified' : sortedOutlets[i].fundProjectState.name
      let isAdmitted = sortedOutlets[i].fundProjectState!==null && sortedOutlets[i].fundProjectState.name == 'Admitted'
      let taxCreditType = sortedOutlets[i].projectCredit.taxCreditType.level + " "
        + sortedOutlets[i].projectCredit.taxCreditType.name;

      let location = this._getLocation(project);
      let projectData:any = {
        project: project.name,
        projectid: project.id,
        projecturl: this.dataFormat.formatLink(project),
        sponsor: project.sponsor[0].sponsor.nickname,
        sponsorurl: this.dataFormat.formatLink(project.sponsor[0].sponsor),
        project_status: this.fetchProjectStatus(project.projectStatuses).name,
        project_status_id: this.fetchProjectStatus(project.projectStatuses).id,
        certificated_name: outlet.certificatedOutlet ? outlet.certificatedOutlet.organization.name : null,
        certificated_outlet_id: outlet.certificatedOutlet ? outlet.certificatedOutlet.organization.id : null,
        certificatedurl: outlet.certificatedOutlet ? this.dataFormat.formatLink(outlet.certificatedOutlet) : null,
        fund_name: outlet.fund ? outlet.fund.name : null,
        fund_id: outlet.fund ? outlet.fund.id : null,
        fundurl: outlet.fund ? this.dataFormat.formatLink(outlet.fund) : null,
        ownership: ((outlet.estimOwnership && outlet.estimOwnership!== null) ? outlet.estimOwnership : (outlet.ownership || 0)) * .01,
        estimated_gross_equity: outlet.estimated_gross_equity,
        credit_type: taxCreditType,
        gross_equity: this.fundService.setGrossEquity(outlet, credit,credit.certificated, this.fetchProjectStatus(project.projectStatuses).name),
        revenue_year: outlet.revenue_year,
        admitted_date: outlet.admitted_date,
        ppc: this.fundService.setPpc(project, credit, outlet, this.fetchProjectStatus(project.projectStatuses).name),
        credit_id: credit.id,
        taggedParticipants: project.taggedParticipants,
        loadWeight: outlet.fund!==null ? outlet.fund.non_certificated_load : outlet.certificatedOutlet!==null ? outlet.certificatedOutlet.certificated_load : 0,
        location: location.locationString,
        county: location.county,
        credit_date: project.ltmodels[0]?.kpiset.kpiset_detail?.credit_start_date ?? null,
        debt: this.fundService.setHardDebt(project, this.fetchProjectStatus(project.projectStatuses).name),
        warehouse: outlet.warehoused,
        closing_date: project.pipelineProjects[0].closingDate[0]?.date ?? null,
        notes: project.projectDetail.syndications_notes,
        subCredits: [],
        isExpanded: false,
      }

      if(dataObject.hasOwnProperty(state)){
        if(dataObject[state].hasOwnProperty(projectData.project_status)){
          let hasSecondaryCredit = dataObject[state][projectData.project_status].find((ob:any) => {
            if((projectData.projectid === ob.projectid) && (ob.credit_type.split(" ")[0]===projectData.credit_type.split(" ")[0])){
              if((projectData.fund_id!==null && ob.fund_id!==null) && (projectData.fund_id == ob.fund_id)){
                return ob;
              }
              if((projectData.certificated_outlet_id!==null && ob.certificated_outlet_id!==null) && (projectData.certificated_outlet_id == ob.certificated_outlet_id)){
                return ob;
              }
              if((projectData.certificated_outlet_id==null && ob.certificated_outlet_id==null) && (projectData.fund_id==null && ob.fund_id==null)){
                return ob;
              }
            }
          })

          if(hasSecondaryCredit){
            let sameSubCred = hasSecondaryCredit.subCredits.filter((sub:any) => sub.credit_id == projectData.credit_id)
            if(hasSecondaryCredit.credit_id!==projectData.credit_id && sameSubCred.length==0){
              hasSecondaryCredit.subCredits.push(projectData);
            }
          } else {
            dataObject[state][projectData.project_status].push(projectData)
            dataObject[state][projectData.project_status].sort((a:any, b:any) => a.project < b.project ? -1 : 1)
          }
        } else {
          dataObject[state][projectData.project_status] = []
          dataObject[state][projectData.project_status].push(projectData)
          dataObject[state][projectData.project_status].sort((a:any,b:any) => a.project < b.project ? -1 : 1)
        }
      } else {
        dataObject[state] = {}
        dataObject[state][projectData.project_status] = []
        dataObject[state][projectData.project_status].push(projectData)
        dataObject[state][projectData.project_status].sort((a:any, b:any) => a.project < b.project ? -1 : 1)
      }

    }
    this._subSort(dataObject)
    let obj = {...this._subSort(dataObject)}
    let sortedObj = {...this._compareSort(obj)}
    return sortedObj;
  }

  private _combineCredts(outlets:any){
    let projectCredits:any[] = []
    let filteredOutlets:any[] = []
    for(let f = 0; f < outlets.length; f++){
      if(outlets[f].projectCredit!==null){
        projectCredits.push(outlets[f].projectCredit)
      }
    }
    projectCredits.forEach((prc:any) => {

      let localCreditOutlets = outlets.filter((outlet:any) => outlet.projectCredit.id == prc.id)
      if(localCreditOutlets.length>0){
        let totalOwnership = 0;
        localCreditOutlets.forEach((lco:any) => {
          totalOwnership += lco.ownership
        })

        if(totalOwnership > 98.0){
          localCreditOutlets.forEach((loct:any) => {
            if(loct.fundProjectState!==null && loct.fundProjectState.name!=='Admitted'){
              filteredOutlets.push(loct)
            }
          })
        } else {
          localCreditOutlets.forEach((loct:any) => {
            let oldPc = {...loct, val: 'old'}
            let newPc = {...loct, estimOwnership: 100 - loct.ownership, val: 'new'}
            // if(newPc.fundProjectState !== null && (newPc.fundProjectState?.name == 'Exited' || newPc.fundProjectState?.name == 'Admitted') ){
            //   newPc.fundProjectState = null
            // }
            if(newPc.fundProjectState !== null){
              newPc.fundProjectState = null
            }
            newPc.fund = null
            newPc.certificatedOutlet = null
            filteredOutlets.push(newPc)
            filteredOutlets.push(oldPc)
            // if(oldPc.fundProjectState !==null && oldPc.fundProjectState.name == 'Admitted'){
            //   oldPc.fundProjectState = null
            //   filteredOutlets.push(oldPc)
            // }
          })
        }
      }
    })

    return filteredOutlets


  }

  private _sortSubCredits(credits:any){
    let sortedOrder:any = []
    let values = [...credits]
    sortedOrder = values.sort((k1:any,k2:any) => {
      let k1Type = k1.projectCredit.taxCreditType.level + " " + k1.projectCredit.taxCreditType.name;
      let k2Type = k2.projectCredit.taxCreditType.level + " " + k2.projectCredit.taxCreditType.name;
      const indexA = this.creditOrder.indexOf(k1Type)
      const indexB = this.creditOrder.indexOf(k2Type)
      if(indexA === -1 && indexB === -1){
        return k1Type.localeCompare(k2Type)
      } else if (indexA === -1){
        return 1
      } else if (indexB === -1){
        return -1;
      } else {
        return indexA - indexB
      }
    })
    return sortedOrder
  }

  private _getLocation(project:any){
    let location = {locationString: "", county: ""}
    if(project.properties.length>0){
      if(project.properties[0].entity.entityAddresses.length> 0){
        let address = project.properties[0].entity.entityAddresses[0].address
        location.locationString += address.zipCode.city
        location.locationString += ', '
        location.locationString += address.zipCode.state.abbreviation
        location.county = address.zipCode.county.name
      }
    }
    return location
  }

  public fetchProjectStatus = (statuses:any[]) => {
    let foundStatus = statuses.find((status:any) => {
      return status.active && status.statusSequence.name == 'Syndications'
    })
    if(foundStatus){
      return {name: foundStatus.status.name, id: foundStatus.id}
    } else {
      let activeStatus = statuses.filter((status:any) => status.active==true);
      return {name: activeStatus[0].status.name, id: activeStatus[0].id}
    }
  }

  public updateSyndicationsPipeline(credit:any, mode: UpdateSyndicationsPipelineMode){
    if(mode===UpdateSyndicationsPipelineMode.ADD){
      this._credits.push(credit)
      this._setCredits(this._credits)
      let projects = this._setSyndicationsProjects(this._credits);
      this._setProjects(projects)
    }
    if(mode===UpdateSyndicationsPipelineMode.UPDATE){
      let updatedCreditList = this._credits.map((cr) => cr.id === credit.id ? credit : cr)
      this._setCredits(updatedCreditList)
      let projects = this._setSyndicationsProjects(updatedCreditList);
      this._setProjects(projects)
    }
    if(mode===UpdateSyndicationsPipelineMode.REMOVE){
      let updatedCreditList = this._credits.filter((cr) => cr.id !== credit)
      this._setCredits(updatedCreditList)
      let projects = this._setSyndicationsProjects(updatedCreditList);
      this._setProjects(projects)
    }
  }
}
